package fr.manyverse.app.domain.image

/**
 * Describes WHERE an image lives in storage: its bucket, its path scheme, and how to
 * clean up previous versions. Centralizing this here lets the storage repository expose a
 * single generic [fr.manyverse.app.domain.repository.ImageStorageRepository.upload] /
 * [fr.manyverse.app.domain.repository.ImageStorageRepository.delete] pair instead of one
 * method per feature. Adding a new image use case = adding a variant here.
 */
sealed interface ImageTarget {
    val bucket: String

    /** Full storage path (inside [bucket]) for an image with the given [extension]. */
    fun buildPath(extension: String): String

    /** How previous versions of this image are removed before/at upload. */
    fun cleanup(): ImageCleanup

    /** Every path this image could occupy, across supported extensions (for cache invalidation). */
    fun candidatePaths(): List<String> = ALL_EXTENSIONS.map { buildPath(it) }

    data class CharacterAvatar(val userId: String, val characterId: String) : ImageTarget {
        override val bucket = AVATARS_BUCKET
        override fun buildPath(extension: String) = "$userId/$characterId.$extension"
        // Folder scan also catches legacy timestamped files ("{characterId}_{ts}.ext").
        override fun cleanup() = ImageCleanup.Folder(folder = userId, namePrefix = characterId)
    }

    data class PreMade(val tableId: String, val preMadeId: String) : ImageTarget {
        override val bucket = AVATARS_BUCKET
        override fun buildPath(extension: String) = "premades/$tableId/$preMadeId.$extension"
        override fun cleanup() = ImageCleanup.Paths(candidatePaths())
    }

    data class InventoryItem(val characterId: String, val itemId: String) : ImageTarget {
        override val bucket = INVENTORY_BUCKET
        override fun buildPath(extension: String) = "items/$characterId/$itemId.$extension"
        override fun cleanup() = ImageCleanup.Paths(candidatePaths())
    }

    data class GmInventoryItem(val tableId: String, val itemId: String) : ImageTarget {
        override val bucket = INVENTORY_BUCKET
        override fun buildPath(extension: String) = "gm_inventory/$tableId/$itemId.$extension"
        override fun cleanup() = ImageCleanup.Paths(candidatePaths())
    }

    data class PreMadeInventoryItem(val tableId: String, val itemId: String) : ImageTarget {
        override val bucket = INVENTORY_BUCKET
        override fun buildPath(extension: String) = "premade_items/$tableId/$itemId.$extension"
        override fun cleanup() = ImageCleanup.Paths(candidatePaths())
    }

    data class Announcement(val tableId: String, val imageId: String) : ImageTarget {
        override val bucket = ANNOUNCEMENTS_BUCKET
        override fun buildPath(extension: String) = "$tableId/$imageId.$extension"
        override fun cleanup() = ImageCleanup.Paths(emptyList())
    }

    companion object {
        const val AVATARS_BUCKET = "avatars"
        const val INVENTORY_BUCKET = "inventory"
        const val ANNOUNCEMENTS_BUCKET = "announcements"
        val ALL_EXTENSIONS = listOf("jpg", "jpeg", "png", "webp")
    }
}

/** Strategy for removing previous versions of an [ImageTarget]'s image. */
sealed interface ImageCleanup {
    /** Delete each of these exact paths (deterministic schemes). */
    data class Paths(val paths: List<String>) : ImageCleanup

    /** List [folder] and delete entries named "{namePrefix}." or "{namePrefix}_" (legacy avatars). */
    data class Folder(val folder: String, val namePrefix: String) : ImageCleanup
}
